// Phase 130 §4c — top-level /sandbox handoff entry route.
//
// The marketing site's CTA points at app://sandbox?handoff=<jwt> regardless
// of auth state. This page:
//
//   1. Captures `?handoff=<jwt>` to sessionStorage (one-shot, see
//      handoff-storage.ts) so the JWT survives the WorkOS round-trip for
//      unauthed visitors.
//   2. If unauthed → fires WorkOS AuthKit's `signUp()`. After signup, WorkOS
//      returns to /callback → /, RootRedirect handles normal post-login
//      routing. The existing X-Handoff-JWT-on-first-authed-request mechanism
//      in workspace-context delivers the JWT to backend's applyHandoffIfPresent
//      and adopts the demo session during signup/bootstrap.
//   3. If authed → forward to `/` so RootRedirect runs the same post-login
//      routing as a fresh signup (no special-case landing surface for the
//      handoff flow). If the user navigates to /w/<ws>/sandbox afterwards,
//      the SandboxPage's claim effect picks up the stored JWT and adopts.
//
// Lives OUTSIDE ProtectedRoute (mirrors /signup) because case 2 is the
// unauthed entry point.

import { useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useSession } from '@/contexts/session-context';
import { setHandoffJwt } from '@/lib/handoff-storage';

export function SandboxHandoffEntryPage() {
  const [searchParams] = useSearchParams();
  const { isLoading, user, signIn } = useSession();

  useEffect(() => {
    const jwt = searchParams.get('handoff');
    if (jwt && jwt.length > 0) {
      setHandoffJwt(jwt);
    }

    if (isLoading) return;
    if (!user) {
      signIn();
    }
  }, [isLoading, searchParams, signIn, user]);

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-base text-muted-foreground">Redirecting to sign up...</p>
    </div>
  );
}
